import React, { useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import PopupPhoneField from "../Fields/PopupPhoneField";
import QuestionsApi from "../../api/questions-api";
import { getLanguage, strings } from "../../utils/localization";
import AcceptTermsFooter from "../../v2/components/Footer/AcceptTermsFooter";
import useAnalytics from "../../utils/useAnalytics";
import hiddenFooterLocationIds from "../../utils/hiddenFooterLocationIds";
import { usePopup } from "./hooks/usePopup";
import { getPopupContent } from "./utils/popupUtils";
import {
  ConfirmationAnimation,
  CloseButtonComponent,
  Logo,
} from "./components/PopupComponents";
import {
  DarkBackground,
  PopupMainContainer,
  PopupContainer,
  LeftSide,
  RightSide,
  PopupImage,
  Overlay,
  Subtitle,
  Header,
  Description,
  SubmitButton,
} from "./styles";

const Popup = () => {
  const wrapperRef = useRef(null);
  const { lang, token: locationId } = useParams();
  const normalizedLang = lang === "es" ? "sp" : lang;

  const {
    visible,
    popupData,
    sentNumber,
    parentURL,
    phoneNumber,
    disable,
    setSentNumber,
    handlePhoneChange,
    handlePhoneNumberPaste,
    close,
    loadPopupData,
    loadParentURL,
  } = usePopup(normalizedLang);

  const {
    sendCommSubmissionEvent,
    sendCommEngagementEvent,
    sendCtaInteractionEvent,
    isInitialized,
  } = useAnalytics(popupData?.GATrackingNumber);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        close();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [close]);

  useEffect(() => {
    const languageValue = normalizedLang || "en";
    strings.setLanguage(languageValue === "sp" ? "es" : languageValue);

    if (popupData && isInitialized) {
      sendCommEngagementEvent({ widget: "Exit Intent", status: "load" });
    }
  }, [popupData, normalizedLang, sendCommEngagementEvent, isInitialized]);

  useEffect(() => {
    loadPopupData();
    loadParentURL();
  }, [locationId]);

  const handleSubmit = async () => {
    const param = {
      phone_number: phoneNumber,
      link: parentURL,
      question: "Exit intent call to action",
      language: getLanguage(normalizedLang),
    };

    sendCommSubmissionEvent({ widget: "Exit Intent" });
    await QuestionsApi.exitIntent({ locationId, param });
    setSentNumber(true);
    setTimeout(close, 2000);
  };

  const handleClick = () => {
    sendCtaInteractionEvent({ widget: "Exit Intent" });
  };

  if (!popupData) return null;

  const { header, title, description, buttonTexts, countryCode } =
    getPopupContent(popupData, normalizedLang);

  return (
    visible && (
      <div onClick={handleClick}>
        <DarkBackground />
        <PopupMainContainer hasImage={popupData.photo}>
          <PopupContainer
            backgroundColor={popupData.backgroundColor}
            ref={wrapperRef}
          >
            <LeftSide hasImage={popupData.photo}>
              {!sentNumber ? (
                <div>
                  <CloseButtonComponent onClose={close} />
                  <Subtitle color={popupData.headerColor}>{header}</Subtitle>
                  <Header color={popupData.titleColor}>{title}</Header>
                  <Description color={popupData.descriptionColor}>
                    {description}
                  </Description>
                  <PopupPhoneField
                    initialCountry={countryCode?.toLowerCase() || "us"}
                    handleChange={handlePhoneChange}
                    getPhoneNumberFromClipboard={(e) =>
                      handlePhoneNumberPaste(e, countryCode)
                    }
                    value={phoneNumber}
                  />
                  <SubmitButton
                    onClick={handleSubmit}
                    disabled={disable}
                    backgroundColor={popupData.buttonColor}
                    color={popupData.buttonTextColor}
                  >
                    {buttonTexts}
                  </SubmitButton>
                  <AcceptTermsFooter
                    color={popupData.buttonColor}
                    termsLink={popupData.termsConditionsLink}
                  />
                </div>
              ) : (
                <ConfirmationAnimation />
              )}
              <Logo isHidden={hiddenFooterLocationIds?.includes(locationId)} />
            </LeftSide>
            {popupData.photo && (
              <RightSide>
                <PopupImage src={popupData.photo} alt={popupData.title} />
                <Overlay buttonColor={popupData.buttonColor} />
              </RightSide>
            )}
          </PopupContainer>
        </PopupMainContainer>
      </div>
    )
  );
};

export default Popup;
